import {
  CompanyDayDriverStat_Read,
  Driver_Read,
  Driver_Read_Minimal,
  Driver_Read_Nested,
  Driver_Read_Typeahead,
  GpsStatus,
  StytchMemberStatus,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import parsePhoneNumber from 'libphonenumber-js';

import { CompanyBasic } from '~hooks/useCompany/models';
import { BasicEquipment, BasicEquipmentDefault } from '~hooks/useEquipment/models';
import { BasicLabel } from '~hooks/useLabels/models';
import { Nullable } from '~types/Nullable';

import { CompanyShareUserBasic } from '../useCompanyShares/models';

export class DriverMinimal {
  constructor(
    private _id: string,
    private _firstName: string,
    private _lastName: string,
  ) {}

  public get id() {
    return this._id;
  }

  public get firstName() {
    return this._firstName;
  }

  public get lastName() {
    return this._lastName;
  }

  public get fullName() {
    return `${this._firstName} ${this._lastName}`;
  }

  public static parse(proto: Driver_Read_Minimal) {
    return new DriverMinimal(proto.id, proto.first_name, proto.last_name);
  }
}

export class DriverTypeahead {
  constructor(
    private _id: string,
    private _first_name: string,
    private _last_name: string,
    private _company: CompanyBasic,
    private _managed: boolean,
    private _company_share: Nullable<CompanyShareUserBasic>,
    private _equipment: Nullable<BasicEquipment>,
    private _schedule_on: boolean,
    private _default_equipment: Nullable<BasicEquipmentDefault>,
    private _company_day_driver_stat: Nullable<CompanyDayDriverStat>,
    private _gps_status: Nullable<GpsStatus>,
    private _labels: BasicLabel[] = [],
    private _compliant: boolean | null = null,
  ) {}

  public get id(): string {
    return this._id;
  }

  public get firstName(): string {
    return this._first_name;
  }

  public get lastName(): string {
    return this._last_name;
  }

  public get fullName() {
    return `${this.firstName} ${this.lastName}`;
  }

  public get company(): CompanyBasic {
    return this._company;
  }

  public get managed(): boolean {
    return this._managed;
  }

  public get companyShare(): Nullable<CompanyShareUserBasic> {
    return this._company_share;
  }

  public get equipment(): Nullable<BasicEquipment> {
    return this._equipment;
  }

  public set equipment(equipment: Nullable<BasicEquipment>) {
    this._equipment = equipment;
  }

  public get scheduleOn(): boolean {
    return this._schedule_on;
  }

  public get defaultEquipment(): Nullable<BasicEquipmentDefault> {
    return this._default_equipment;
  }

  public set defaultEquipment(defaultEquipment: Nullable<BasicEquipmentDefault>) {
    this._default_equipment = defaultEquipment;
  }

  public get companyDayDriverStat(): Nullable<CompanyDayDriverStat> {
    return this._company_day_driver_stat;
  }

  public get gpsStatus() {
    return this._gps_status;
  }

  public get labels() {
    return this._labels;
  }

  public get compliant() {
    return this._compliant;
  }

  public static parse(proto: Driver_Read_Typeahead) {
    return new DriverTypeahead(
      proto.id,
      proto.first_name,
      proto.last_name,
      CompanyBasic.parse(proto.company),
      proto.managed,
      proto.company_share ? CompanyShareUserBasic.parse(proto.company_share) : null,
      proto.equipment ? BasicEquipment.parse(proto.equipment) : null,
      proto.schedule_on,
      proto.default_equipment
        ? BasicEquipmentDefault.parse(proto.default_equipment)
        : null,
      proto.company_day_driver_stat
        ? CompanyDayDriverStat.parse(proto.company_day_driver_stat)
        : null,
      proto.gps_status || null,
      proto.labels.map((label) => BasicLabel.parse(label)),
      proto.compliant ?? null,
    );
  }
}

export class DriverBasic extends DriverTypeahead {
  constructor(
    _id: string,
    _first_name: string,
    _last_name: string,
    _company: CompanyBasic,
    _managed: boolean,
    _company_share: Nullable<CompanyShareUserBasic>,
    _equipment: Nullable<BasicEquipment>,
    private _email: Nullable<string>,
    private _phone: Nullable<string>,
    _schedule_on: boolean,
    _gps_status: Nullable<GpsStatus> = null,
    _company_day_driver_stat: Nullable<CompanyDayDriverStat> = null,
    _labels: BasicLabel[] = [],
    _compliant: boolean | null = null,
  ) {
    super(
      _id,
      _first_name,
      _last_name,
      _company,
      _managed,
      _company_share,
      _equipment,
      _schedule_on,
      _company_share?.defaultEquipment || null,
      _company_day_driver_stat || null,
      _gps_status,
      _labels,
      _compliant,
    );
  }

  public get email(): Nullable<string> {
    return this._email;
  }

  public get phone(): Nullable<string> {
    if (this._phone) {
      const phone = this._phone.replace(/^\+/g, '');
      return parsePhoneNumber(`+${phone}`)?.formatInternational() as string;
    }
  }

  public static parse(proto: Driver_Read_Nested) {
    return new DriverBasic(
      proto.id,
      proto.first_name,
      proto.last_name,
      CompanyBasic.parse(proto.company),
      proto.managed,
      proto.company_share ? CompanyShareUserBasic.parse(proto.company_share) : null,
      proto.equipment ? BasicEquipment.parse(proto.equipment) : null,
      proto.email,
      proto.phone,
      proto.schedule_on,
      proto.gps_status || null,
      proto.company_day_driver_stat
        ? CompanyDayDriverStat.parse(proto.company_day_driver_stat)
        : null,
      proto.labels.map((label) => BasicLabel.parse(label)),
      proto.compliant ?? null,
    );
  }
}

export class Driver extends DriverBasic {
  constructor(
    _id: string,
    _first_name: string,
    _last_name: string,
    _company: CompanyBasic,
    _managed: boolean,
    _company_share: Nullable<CompanyShareUserBasic>,
    _equipment: Nullable<BasicEquipment>,
    _email: Nullable<string>,
    _phone: Nullable<string>,
    private _additional_equipment: BasicEquipment[],
    _schedule_on: boolean,
    private _stytch_member_status: StytchMemberStatus,
    private _stytch_user_status: StytchMemberStatus,
    private _external_id: Nullable<string> = null,
    private _time_zone: Nullable<string> = null,
    private _created_at: Nullable<string> = null,
    private _updated_at: Nullable<string> = null,
    private _default_additional_equipment: BasicEquipmentDefault[],
    _gps_status: Nullable<GpsStatus> = null,
    _labels: BasicLabel[] = [],
    _compliant: boolean | null = null,
    private _hide_rates: boolean = false,
    private _intrastate_hours_of_service: boolean = false,
  ) {
    super(
      _id,
      _first_name,
      _last_name,
      _company,
      _managed,
      _company_share,
      _equipment,
      _email,
      _phone,
      _schedule_on,
      _gps_status,
      null,
      _labels,
      _compliant,
    );
  }

  public get additionalEquipment(): BasicEquipment[] {
    return this._additional_equipment;
  }

  public set additionalEquipment(equipment: BasicEquipment[]) {
    this._additional_equipment = equipment;
  }

  public get externalId(): Nullable<string> {
    return this._external_id;
  }

  public get timeZone(): Nullable<string> {
    return this._time_zone;
  }

  public get createdAt(): Nullable<string> {
    return this._created_at;
  }

  public get updatedAt(): Nullable<string> {
    return this._updated_at;
  }

  public get defaultAdditionalEquipment() {
    return this._default_additional_equipment;
  }

  public set defaultAdditionalEquipment(value: BasicEquipmentDefault[]) {
    this._default_additional_equipment = value;
  }

  public get hideRates() {
    return this._hide_rates;
  }

  public get intrastateHoursOfService() {
    return this._intrastate_hours_of_service;
  }

  /**
   * An active member is someone that is an active Stytch Member, meaning they have been connected by email
   */
  public get isActiveMember() {
    return this._stytch_member_status === StytchMemberStatus.ACTIVE;
  }

  /**
   * An active user is someone that is an active Stytch User, meaning they have been connected by phone
   */
  public get isActiveUser() {
    return this._stytch_user_status === StytchMemberStatus.ACTIVE;
  }

  public static parse(proto: Driver_Read) {
    const driver = new Driver(
      proto.id,
      proto.first_name,
      proto.last_name,
      CompanyBasic.parse(proto.company),
      proto.managed,
      proto.company_share ? CompanyShareUserBasic.parse(proto.company_share) : null,
      proto.equipment ? BasicEquipment.parse(proto.equipment) : null,
      proto.email,
      proto.phone,
      proto.additional_equipment.map((equipment) => BasicEquipment.parse(equipment)),
      proto.schedule_on,
      proto.stytch_member_status,
      proto.stytch_user_status,
      proto.external_id || null,
      proto.time_zone || null,
      (proto as unknown as { created_at: string }).created_at || null,
      (proto as unknown as { updated_at: string }).updated_at || null,
      proto.default_additional_equipment.map((equipment) =>
        BasicEquipmentDefault.parse(equipment),
      ),
      proto.gps_status || null,
      proto.labels.map((label) => BasicLabel.parse(label)),
      proto.compliant ?? null,
      proto.hide_rates ?? false,
      proto.intrastate_hours_of_service,
    );
    if (proto.default_equipment) {
      driver.defaultEquipment = BasicEquipmentDefault.parse(proto.default_equipment);
    }
    return driver;
  }
}

export class CompanyDayDriverStat {
  constructor(
    private _id: string,
    private _createdAt: Dayjs,
    private _updatedAt: Dayjs,
    private _day: Nullable<string>,
    private _jobsCount: number,
  ) {}

  public get id() {
    return this._id;
  }

  public get createdAt() {
    return this._createdAt;
  }

  public get updatedAt() {
    return this._updatedAt;
  }

  public get day() {
    return this._day;
  }

  public get jobsCount() {
    return this._jobsCount;
  }

  public static parse(proto: CompanyDayDriverStat_Read) {
    return new CompanyDayDriverStat(
      proto.id,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      proto.day ?? null,
      proto.jobs_count ?? 0,
    );
  }
}
